import { ChangeDetectionStrategy, Component, Input } from '@angular/core'
import { PasswordComplexityPolicy } from 'libs/shared/models/shared.models'
import { TranslateService } from '@ngx-translate/core'
import { Observable } from 'rxjs'

/**
 * regex for password complexity returned by the API
 *   '(?=(?:[^a-z]*[a-z]){1,})' -> at least 1 lower cases anywhere
 *   '(?=(?:[^A-Z]*[A-Z]){1,})' -> at least 1 upper cases anywhere
 *   '(?=(?:[^0-9]*[0-9]){1,})' -> at least 1 digits anywhere
 *   '(?=(?:[^\W]*[\W]){1,})'   -> at Least 1 special characters anywhere
 *   '.{8,}'                    -> at least 8 characters long
 *
 *
 *   i.e for 2 lower case, 2 upper case, 2 digits, 2 special characters and 8 characters long
 *
 *   '(?=(?:[^a-z]*[a-z]){2,})' -> at least 2 lower cases anywhere
 *   '(?=(?:[^A-Z]*[A-Z]){2,})' -> at least 2 upper cases anywhere
 *   '(?=(?:[^0-9]*[0-9]){2,})' -> at least 2 digits anywhere
 *   '(?=(?:[^\W]*[\W]){2,})'   -> at least 2 special characters anywhere
 *   '.{8,}'                    -> at least 8 characters long
 */
@Component({
  selector: 'gb-password-hint',
  templateUrl: './gb-password-hint.component.html',
  styleUrls: ['./gb-password-hint.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPasswordHintComponent {
  @Input()
  passwordComplexityPolicy$!: Observable<PasswordComplexityPolicy | null>
  @Input() password: string | null = ''
  matIconDone = 'check_circle'
  matIconError = 'error'

  translationKeys = new Map<string, string>()
    .set('min_length', 'PASSWORD_HINT.MIN_LENGTH')
    .set('min_lowercase', 'PASSWORD_HINT.MIN_LOWERCASE')
    .set('min_uppercase', 'PASSWORD_HINT.MIN_UPPERCASE')
    .set('min_number', 'PASSWORD_HINT.MIN_NUMBER')
    .set('min_special_chars', 'PASSWORD_HINT.MIN_SPECIAL_CHARS')

  constructor(private translateService: TranslateService) {}

  validatesRegex(regex: string): boolean {
    if (!this.password) {
      return false
    }
    return new RegExp(regex).test(this.password)
  }

  getKeys(pwdPolicy: PasswordComplexityPolicy): string[] {
    return Object.keys(pwdPolicy)
  }

  getPwdPolicyUnit(pwdPolicy: PasswordComplexityPolicy, key: string): number {
    return pwdPolicy[key as keyof PasswordComplexityPolicy].unit
  }
  getPwdPolicyRegex(pwdPolicy: PasswordComplexityPolicy, key: string): string {
    return pwdPolicy[key as keyof PasswordComplexityPolicy].regex
  }
  getPwdPolicyTranslationKey(key: string): string {
    return this.translationKeys.get(key) || ''
  }
  getPwdPolicyTranslation(key: string, unit: number): string {
    return this.translateService.instant(this.getPwdPolicyTranslationKey(key), {
      unit: unit,
    })
  }
}
